import { useState, useMemo } from "react";
import { Grid, Stack, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Navbar from "../../components/Navbar";
import { useCollection } from "../../hooks/useCollection";
import { useDocs } from "../../hooks/useDocs";
import { useAuth } from "../../hooks/useAuth";
import "./Instances.css";
import { emitToast } from "../../common/toastEmitter";
import DialogBox from "../../components/Common/DialogBox";
import InstanceForm from "./InstanceForm";
import { useInstances } from "../../hooks/useInstances";
import BackdropLoader from "../../components/Common/BackdropLoader";

const defaultInstances = {
  id: null,
  url: null,
  apiKey: null,
  secretKey: null,
};

export default function Instances() {
  const [loading, setLoading] = useState(false);
  const { user, authIsReady } = useAuth();
  const enabled = !!authIsReady;
  const isUserAdmin = !!user?.admin;
  const { documents } = useCollection("demoInstances", enabled);
  const [formState, setFormState] = useState(defaultInstances);
  const [selectedRow, setSelectedRow] = useState(null);
  const { addInstance, deleteDocument, updateUnapprovedUsers } = useDocs();
  const [openDialog, setOpenDialog] = useState(false);
  const { getAccessForInstance } = useInstances();
  const { documents: partnerDetails } = useCollection("partnerDetails", enabled && isUserAdmin);
  const { documents: admins } = useCollection("user_admin", enabled && isUserAdmin);

  const columns = useMemo(
    () => [
      { field: "url", headerName: "INSTANCES URL", width: 200 },
      { field: "email", headerName: "ADDED BY", width: 200 },
    ],
    []
  );

  const instances = useMemo(() => {
    if (documents) {
      let processedData = [];
      if (!user?.admin) {
        processedData = documents.map((item) => ({
          id: item.id,
          email: user.email,
          url: item.url,
        }));
      } else {
        processedData = documents.map((item) => ({
          id: item.id,
          email: user.email,
          url: item.url,
        }));
      }
      return processedData;
    }
    return [{ id: "loading", url: "loading" }];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, user]);

  const handleCellClick = async (params) => {
    try {
      const instancesData = documents.find((item) => item.id === params.row.id);
      
      const selectedrowData = {
        id: instancesData?.id || params.row.id,
        url: instancesData?.url || params.row.url,
        apiKey: instancesData?.apiKey || params.row.apiKey,
        secretKey: instancesData?.secretKey || params.row.secretKey,
      };
      setSelectedRow(selectedrowData);
    } catch (err) {
      console.error("Error fetching user data:", err);
    }
  };

  const handleSaveInstances = async (formData) => {
    try {
      setLoading(true);
      const newInstance = {
        url: formData.url || "",
        apiKey: formData.apiKey || "",
        secretKey: formData.secretKey || "",
      };

      await addInstance(formData.id, newInstance);

      if (user.admin) {
        await updateUnapprovedUsers(formData.id, formData.isApproved);
      }

      handleInsanceAccess();
      emitToast("Instance saved successfully.", "success");
      setSelectedRow(null); // reset selected row
      setFormState({
          id: null,
          url: "",
          apiKey: "",
          secretKey: "",
        });
    } catch (error) {
      emitToast(error.message || "Failed to save instance", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredPartners = useMemo(() => {
    if (!partnerDetails) return [{ id: "loading" }];

    const basicFilter = partnerDetails.filter(
      (item) => item.email && !item.email.includes("@tulip.co")
    );

    return basicFilter;
  }, [partnerDetails]);

  const partners = useMemo(() => {
    return filteredPartners.map((partner) => {
      // Check if admins array is loaded and find the matching admin based on the id
      const matchingAdmin = admins?.find((admin) => admin.id === partner.id);
      // Construct the partner object
      const partnerObj = {
        id: partner.id || "loading",
        email: partner.email || "needs lookup",
        approved: partner.approved || false,
        emailVerified: partner?.emailVerified || false,
      };

      // Add the admin property only if a matching admin is found
      if (matchingAdmin) {
        partnerObj.admin = matchingAdmin.admin; // Replace 'adminProperty' with the actual property name from the admin object
      }

      return partnerObj;
    });
  }, [filteredPartners, admins]);

  const handleInsanceAccess = async () => {
    try {
      const users = partners.filter(
        (admin) => admin.approved && admin.emailVerified
      );

      for (const userObj of users) {
        getAccessForInstance(selectedRow.url, {
          role:"operator",
          name: userObj.email,
          badge_id: userObj.email,
        },
        selectedRow.apiKey,
        selectedRow.secretKey);
      }
    } catch (error) {
      console.log("Error granting access to instance:", error);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedRow) {
        await deleteDocument(selectedRow.id, "demoInstances");
        if (user.admin) {
          await updateUnapprovedUsers(selectedRow.id, false);
        }

        setSelectedRow(null);
        setOpenDialog(false);
        emitToast("Demo Instance deleted successfully.", "success");
        setFormState({
          id: null,
          url: "",
          apiKey: "",
          secretKey: "",
        }); // reset form state
      }
    } catch (error) {
      emitToast("Failed to delete instance. Please try again.", "error");
    }
  };

  const handleChange = (updatedRow) => {
    setSelectedRow(updatedRow);
    console.log(updatedRow,"sdfsdfS")
    setFormState(updatedRow); 
  };

  const handleCreateNew = () => {
    setSelectedRow(defaultInstances);
    setFormState(defaultInstances);
  }

  return (
    <>
      <BackdropLoader open={loading} />
      <Navbar sx={{ backgroundColor: "grey" }} />

      <Grid
        container
        gap={2}
        height="100%"
        padding={2}
        className="accounts_grid lg:!flex-nowrap"
      >
        <Grid
          item
          xs={12}
          md={4}
          sx={{ height: "calc(100% - 65px)", backgroundColor: "white" }}
          padding={1}
        >
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            paddingBlock={2}
          >
            <Typography marginTop={1} fontWeight={600}>
              Public Demo Instances
            </Typography>
          </Stack>
          <DataGrid
            rows={instances}
            columns={columns}
            onCellClick={handleCellClick}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 20 } },
            }}
            pageSizeOptions={[20, 40]}
            checkboxSelection={false}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={8}
          sx={{ height: "calc(100% - 65px)", backgroundColor: "white" }}
          padding={1}
        >
          <Stack direction={"row"} justifyContent={"end"}>
            {user?.admin && (
              <Button
                color="primary"
                variant="outlined"
                onClick={handleCreateNew}
              >
                Create New
              </Button>
            )}
          </Stack>

          <InstanceForm
            selectedRow={selectedRow}
            onChange={handleChange}
            onSave={handleSaveInstances}
            isAdmin={user?.admin}
            formState={formState}
            setOpenDialog={setOpenDialog}
          />
        </Grid>
      </Grid>
      <DialogBox
        open={openDialog}
        setOpen={setOpenDialog}
        handleDelete={handleDelete}
        message={"You want to delete the instance?"}
      />
    </>
  );
}
